import { useEffect, useState } from "react";
import { motion, AnimatePresence, LayoutGroup, animate } from "framer-motion";
import { Trophy, Clock, ArrowUp, ArrowDown, Minus, Sparkles } from "lucide-react";
import { Badge } from "./ui/badge";
import { fmtSecs, isTimeTieBreak } from "../lib/liveTime";

const HOLD_MS = 1200;
const SPRING = { type: "spring", stiffness: 260, damping: 28 };

const CountUp = ({ from, to, run }) => {
  const [value, setValue] = useState(from);
  useEffect(() => {
    if (!run) {
      setValue(from);
      return;
    }
    const controls = animate(from, to, { duration: 0.9, ease: "easeOut", onUpdate: (v) => setValue(Math.round(v)) });
    return () => controls.stop();
  }, [from, to, run]);
  return <>{value}</>;
};

const RankBadge = ({ rank }) => (
  <span
    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${
      rank === 1 ? "bg-amber-400 text-amber-950"
        : rank === 2 ? "bg-slate-300 text-slate-800"
        : rank === 3 ? "bg-orange-300 text-orange-900"
        : "bg-muted text-muted-foreground"
    }`}
  >
    {rank}
  </span>
);

const MoveChip = ({ entry }) => {
  if (entry.prev_rank == null) {
    return (
      <span className="inline-flex items-center gap-0.5 text-xs font-semibold text-sky-600 dark:text-sky-400" data-testid={`lb-move-${entry.participant_id}`}>
        <Sparkles className="w-3 h-3" /> new
      </span>
    );
  }
  const delta = entry.prev_rank - entry.rank;
  if (delta > 0) {
    return (
      <span className="inline-flex items-center text-xs font-bold text-green-600 dark:text-green-400" data-testid={`lb-move-${entry.participant_id}`}>
        <ArrowUp className="w-3.5 h-3.5" />{delta}
      </span>
    );
  }
  if (delta < 0) {
    return (
      <span className="inline-flex items-center text-xs font-bold text-red-500 dark:text-red-400" data-testid={`lb-move-${entry.participant_id}`}>
        <ArrowDown className="w-3.5 h-3.5" />{-delta}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center text-xs text-muted-foreground" data-testid={`lb-move-${entry.participant_id}`}>
      <Minus className="w-3.5 h-3.5" />
    </span>
  );
};

export const LiveLeaderboard = ({ entries, title, meId, resetKey, testId = "live-leaderboard" }) => {
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    setRevealed(false);
    const t = setTimeout(() => setRevealed(true), HOLD_MS);
    return () => clearTimeout(t);
  }, [resetKey]);

  const hasPrev = entries.some((e) => e.prev_rank != null);
  const before = [...entries].sort((a, b) => (a.prev_rank ?? 999) - (b.prev_rank ?? 999) || a.rank - b.rank);
  const ordered = revealed || !hasPrev ? entries : before;

  return (
    <div className="bg-card rounded-2xl border border-border p-6" data-testid={testId} data-phase={revealed ? "after" : "before"}>
      <h2 className="font-heading font-bold text-xl mb-4 flex items-center gap-2">
        <Trophy className="w-5 h-5 text-amber-500" /> {title}
      </h2>
      {entries.length === 0 ? (
        <p className="text-sm text-muted-foreground">No players yet.</p>
      ) : (
        <LayoutGroup id={testId}>
          <div className="space-y-2">
            {ordered.map((entry) => {
              const isMe = meId && entry.participant_id === meId;
              const showBefore = hasPrev && !revealed;
              const displayRank = showBefore && entry.prev_rank != null ? entry.prev_rank : entry.rank;
              const scoreBefore = entry.score - (entry.last_points || 0);
              const totalShown = showBefore ? Math.max(0, entry.total_time - (entry.last_time || 0)) : entry.total_time;
              return (
                <motion.div
                  layout
                  transition={SPRING}
                  key={entry.participant_id}
                  className={`flex items-center gap-3 p-3 rounded-xl ${isMe ? "bg-primary/10 border border-primary/30" : "bg-muted/40"}`}
                  data-testid={`lb-row-${entry.participant_id}`}
                >
                  <RankBadge rank={displayRank} />
                  <span className="flex-1 min-w-0">
                    <span className="font-medium truncate flex items-center gap-2">
                      <span className="truncate">
                        {entry.nickname}
                        {entry.is_anonymous && !isMe && <span className="text-xs text-muted-foreground ml-1">(guest)</span>}
                        {isMe && <span className="text-xs text-primary ml-1">(you)</span>}
                      </span>
                      <AnimatePresence>
                        {revealed && hasPrev && (
                          <motion.span initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.5 }}>
                            <MoveChip entry={entry} />
                          </motion.span>
                        )}
                      </AnimatePresence>
                    </span>
                    <span className="text-xs text-muted-foreground flex items-center gap-1 min-h-[1rem]" data-testid={`lb-time-${entry.participant_id}`}>
                      <Clock className="w-3 h-3" />
                      {(revealed || !hasPrev) && entry.last_time != null && (
                        <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}>
                          +{entry.last_points} pts in {fmtSecs(entry.last_time)} ·{" "}
                        </motion.span>
                      )}
                      total {fmtSecs(totalShown) ?? "0.0s"}
                      {(revealed || !hasPrev) && isTimeTieBreak(entries, entry) && (
                        <span className="text-amber-600 dark:text-amber-400">· tie broken on time</span>
                      )}
                    </span>
                  </span>
                  <Badge variant="outline" className="tabular-nums" data-testid={`lb-score-${entry.participant_id}`}>
                    {hasPrev ? <CountUp from={scoreBefore} to={entry.score} run={revealed} /> : entry.score} pts
                  </Badge>
                </motion.div>
              );
            })}
          </div>
        </LayoutGroup>
      )}
    </div>
  );
};
